import type { ReportPicklist } from 'generated/graphql';

type StoredPicklistEntry = {
  picklist: ReportPicklist;
  updatedAt: number;
};

type PicklistClipboardStore = Record<string, StoredPicklistEntry>;

const STORAGE_KEY = 'srna.picklistClipboardStore';
const MAX_ENTRIES = 200;
const ENTRY_TTL_MS = 1000 * 60 * 60 * 24; // 24 hours

const hasLocalStorage = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage != null;
  } catch {
    return false;
  }
};

// Deep clone so persistent copies do not hold references to React state.
const clonePicklist = (picklist: ReportPicklist): ReportPicklist =>
  JSON.parse(JSON.stringify(picklist));

// Prevent unbounded growth by removing expired entries and trimming to MAX_ENTRIES.
const pruneStore = (store: PicklistClipboardStore): PicklistClipboardStore => {
  const now = Date.now();

  for (const [picklistID, entry] of Object.entries(store)) {
    if (entry == null || entry.picklist == null) {
      delete store[picklistID];
      continue;
    }

    if (typeof entry.updatedAt !== 'number' || now - entry.updatedAt > ENTRY_TTL_MS) {
      delete store[picklistID];
    }
  }

  const entryIds = Object.keys(store);
  if (entryIds.length <= MAX_ENTRIES) {
    return store;
  }

  const sortedByAge = entryIds.sort((a, b) => {
    const aTime = store[a]?.updatedAt ?? 0;
    const bTime = store[b]?.updatedAt ?? 0;
    return aTime - bTime;
  });

  for (let i = 0; i < sortedByAge.length - MAX_ENTRIES; i++) {
    delete store[sortedByAge[i]];
  }

  return store;
};

const readStore = (): PicklistClipboardStore => {
  if (!hasLocalStorage()) return {};

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw == null || raw === '') {
      return {};
    }

    const parsed = JSON.parse(raw) as PicklistClipboardStore;
    return pruneStore(parsed);
  } catch {
    return {};
  }
};

const writeStore = (store: PicklistClipboardStore): void => {
  if (!hasLocalStorage()) return;

  try {
    const cleaned = pruneStore(store);
    const hasEntries = Object.keys(cleaned).length > 0;
    if (!hasEntries) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
  } catch {
    // ignore serialization failures
  }
};

// Utility to read/modify/write the clipboard store atomically.
const mutateStore = (mutator: (store: PicklistClipboardStore) => void): void => {
  if (!hasLocalStorage()) return;

  const store = readStore();
  mutator(store);
  writeStore(store);
};

const upsertEntries = (picklists: ReadonlyArray<ReportPicklist>): void => {
  if (picklists == null || picklists.length === 0) return;

  mutateStore((store) => {
    const now = Date.now();

    picklists.forEach((picklist) => {
      if (picklist?.id == null) return;

      store[picklist.id] = {
        picklist: clonePicklist(picklist),
        updatedAt: now,
      };
    });
  });
};

// Persist picklists so their options can be restored after refresh/cross-editor paste.
export const storeClipboardPicklists = (picklists: ReadonlyArray<ReportPicklist>): void => {
  upsertEntries(picklists);
};

export const getPicklistFromClipboard = (picklistID: string): ReportPicklist | undefined => {
  if (!picklistID) return undefined;

  const store = readStore();
  const entry = store[picklistID];

  if (!entry) return undefined;

  return clonePicklist(entry.picklist);
};

export const clearClipboardPicklists = (): void => {
  if (!hasLocalStorage()) return;

  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

// Returns every cached picklist regardless of origin; used on mount to hydrate state.
export const getStoredPicklists = (): ReadonlyArray<ReportPicklist> => {
  const store = readStore();
  return Object.values(store).map((entry) => clonePicklist(entry.picklist));
};
